import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCallIntelligenceWritebackV1Service, serviceName } from '../service';
import { CallWritebackStatusIO, ServiceQueries } from '../types';
import { useCallIntelligenceWritebackV1Queries } from './use-call-intelligence-writeback-v1-queries';
import {
  UseCallIntelligenceWritebackV1QueryEndpointArgs,
  useCallIntelligenceWritebackV1Query,
} from './use-call-intelligence-writeback-v1-query';

/**
 * A hook that returns a query for the `CallWritebackStatus` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useCallWritebackStatusQuery = <E = unknown, D = CallWritebackStatusIO['output']>(
  args: UseCallIntelligenceWritebackV1QueryEndpointArgs<'CallWritebackStatus', E, D>
) =>
  useCallIntelligenceWritebackV1Query({
    endpointName: 'CallWritebackStatus',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `CallWritebackStatus` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useCallWritebackStatusQueries = <E = unknown, D = CallWritebackStatusIO['output']>(
  argsArr: UseCallIntelligenceWritebackV1QueryEndpointArgs<'CallWritebackStatus', E, D>[]
) =>
  useCallIntelligenceWritebackV1Queries(
    argsArr.map((args) => ({
      endpointName: 'CallWritebackStatus',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `CallWritebackStatus` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchCallWritebackStatus = () => {
  const queryClient = useQueryClient();

  const fetchCallWritebackStatus = async <E = unknown, D = CallWritebackStatusIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCallIntelligenceWritebackV1QueryEndpointArgs<'CallWritebackStatus', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      CallWritebackStatusIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'CallWritebackStatus'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        CallWritebackStatusIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'CallWritebackStatus'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'CallWritebackStatus'>({
          serviceName,
          endpointName: 'CallWritebackStatus',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCallIntelligenceWritebackV1Service.CallWritebackStatus(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchCallWritebackStatus;
};
